import { useCallback, useEffect, useRef, useState } from 'react';
import SectionWrapper from './SectionWrapper';
import { Text, Box, Divider, Link } from '@chakra-ui/react';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import TemplateContentField from './TemplateContentField';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  addField,
  reorderField,
  setFieldProp,
} from '../../../store/redux/slices/template.slice';
import {
  FIELD_CONFIG,
  SLOT_LABELS,
  SLOT_ORDER,
} from './utils/fieldVisibilityConfig';
import {
  useTemplateFieldValidation,
  useInitialDuplicateValidation,
} from './validation/useTemplateFieldValidation';
import AddMoreButton from './AddMoreButton';
import StrictModeDroppable from './utils/StrictModeDroppable';

const AdditionalFieldsSection = ({
  isShared,
  isEdit,
}: {
  isShared: boolean;
  isEdit: boolean;
}) => {
  const dispatch = useAppDispatch();
  const templateType = useAppSelector((s) => s.template.templateType);
  const fields = useAppSelector((s) => s.template.fields);
  const backFields = fields.back;
  const config = FIELD_CONFIG[templateType];
  const backLimit = config.back.limit;
  const minRequired = config.back.minRequired;
  const { validateField } = useTemplateFieldValidation('fields');
  const errors = useAppSelector((s) => s.templateErrors);

  useInitialDuplicateValidation(['back'], fields, validateField);

  // Rename default back_* ids/labels to field_* for new templates
  const renamedRef = useRef(false);
  useEffect(() => {
    if (renamedRef.current || isEdit) return;
    renamedRef.current = true;
    for (const [slot, field] of Object.entries(backFields)) {
      if (!field) continue;
      if (field.id === `back_${slot}`) {
        dispatch(
          setFieldProp({
            section: 'back',
            slot,
            key: 'id',
            value: `field_${slot}`,
          })
        );
      }
      const backLabel = `Back ${slot.charAt(0).toUpperCase() + slot.slice(1)}`;
      if (field.label === backLabel) {
        dispatch(
          setFieldProp({
            section: 'back',
            slot,
            key: 'label',
            value: `Field ${slot.charAt(0).toUpperCase() + slot.slice(1)}`,
          })
        );
      }
    }
  }, [backFields, isEdit, dispatch]);

  const fieldEntries = Object.entries(backFields).filter(
    ([, field]) => field !== null
  );
  const isBackLimitReached = fieldEntries.length >= backLimit;
  const isMinRequiredReached = fieldEntries.length > minRequired;
  const isDraggable = fieldEntries.length > 1;

  const [isDragging, setIsDragging] = useState(false);

  // Track open/closed state per field so it follows field data during drag reorder
  const [openFields, setOpenFields] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const slot of SLOT_ORDER) {
      if (backFields[slot] != null) {
        initial[`back.${slot}`] = !isEdit;
      }
    }
    return initial;
  });

  const getFieldOpen = useCallback(
    (slot: string) => openFields[`back.${slot}`] ?? !isEdit,
    [openFields, isEdit]
  );

  const setFieldOpen = useCallback((slot: string, value: boolean) => {
    setOpenFields((prev) => ({ ...prev, [`back.${slot}`]: value }));
  }, []);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (result: DropResult) => {
    setIsDragging(false);
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    // Reorder the open states to follow the field data
    const activeSlots = SLOT_ORDER.filter((s) => backFields[s] != null);
    const openValues = activeSlots.map((s) => getFieldOpen(s));
    const [movedOpen] = openValues.splice(result.source.index, 1);
    openValues.splice(result.destination.index, 0, movedOpen);

    setOpenFields((prev) => {
      const next = { ...prev };
      activeSlots.forEach((s) => {
        delete next[`back.${s}`];
      });
      openValues.forEach((val, i) => {
        next[`back.${SLOT_ORDER[i]}`] = val;
      });
      return next;
    });

    dispatch(
      reorderField({
        section: 'back',
        fromIndex: result.source.index,
        toIndex: result.destination.index,
      })
    );
  };

  return (
    <SectionWrapper
      id="fields-section"
      title="Additional fields"
      tooltip={
        <Text textAlign="center">
          This content shows on the back of the pass for Apple, and further down
          on scroll for Google.
        </Text>
      }
      openOnLoad
      subtitle={
        !isShared ? (
          "Additional fields are auto-populated from Shared. These values apply to all platforms and can't be changed per device."
        ) : (
          <Text fontSize="15px">
            Add up to {backLimit} fields, plain text or clickable links. See{' '}
            <Link
              href="https://docs.passentry.com/documentation/pass-template-types-explained?assistant=what%20text%20customization%20options%20do%20I%20have%20for%20the%20back%20of%20the%20pass?#can-i-add-a-clickable-link-to-a-back-field"
              isExternal
              color="linkBlue"
              whiteSpace="nowrap"
              _hover={{ textDecoration: 'underline' }}
            >
              advanced formatting &rarr;
            </Link>
          </Text>
        )
      }
    >
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <StrictModeDroppable droppableId="back" type="back">
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={
                isDragging
                  ? {
                      cursor: 'grabbing',
                      '& *': {
                        cursor: 'grabbing !important',
                      },
                    }
                  : undefined
              }
            >
              {fieldEntries.map(([slot, field], fieldIndex) => (
                <Box key={`back-${slot}`}>
                  {fieldIndex > 0 && !isDragging && (
                    <Divider borderColor="lightGrey" my={4} />
                  )}
                  <Draggable
                    draggableId={`back-${slot}`}
                    index={fieldIndex}
                    isDragDisabled={!isDraggable || !isShared}
                  >
                    {(dragProvided) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                      >
                        <TemplateContentField
                          label={`Field ${SLOT_LABELS[slot] ?? slot}`}
                          section="back"
                          slot={slot}
                          newlyAdded={field._newlyAdded}
                          validateField={validateField}
                          errors={errors}
                          isMinRequiredReached={isMinRequiredReached}
                          isShared={isShared}
                          isEdit={isEdit}
                          autoGenerateFieldId
                          isDraggable={isDraggable && isShared}
                          dragHandleProps={dragProvided.dragHandleProps}
                          controlledOpen={getFieldOpen(slot)}
                          onOpenChange={(value) => setFieldOpen(slot, value)}
                          showLinkToggle={isShared}
                        />
                      </Box>
                    )}
                  </Draggable>
                </Box>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </StrictModeDroppable>
      </DragDropContext>
      {backLimit > 0 && !isBackLimitReached && isShared && (
        <Box mt={4}>
          {fieldEntries.length > 0 && (
            <Divider borderColor="lightGrey" mb={4} />
          )}
          <AddMoreButton
            onClick={() => {
              const nextSlot = SLOT_ORDER.find((s) => backFields[s] == null);
              if (nextSlot) {
                setFieldOpen(nextSlot, true);
              }
              dispatch(addField({ section: 'back', idPrefix: 'field' }));
            }}
            text="Add additional field"
          />
        </Box>
      )}
    </SectionWrapper>
  );
};

export default AdditionalFieldsSection;
